import React from 'react';
import { find, map, times, toString, parseInt, noop, split, trim, isEmpty, get } from 'lodash';

// local dependencies
import { getLabel } from 'utils/getLabel';
import { Icon } from 'modules/icon-bank';
import { CREDIT_CARD_TYPE } from 'constants/entity-names';
import { SingleSelect } from 'modules/singleSelect';
import { GLOBAL_LABELS } from 'constants/common';
import moment from 'moment';
import { hsaFsaIndicator as HsaFsaIndicator } from 'ui/views/widgets/hsaFsaIndicator';

// card type icons
const CARD_TYPE_ICON = {
    [ CREDIT_CARD_TYPE.CREDIT_VISA ]: 'visaCard',
    [ CREDIT_CARD_TYPE.MASTER_CARD ]: 'masterCard',
    [ CREDIT_CARD_TYPE.AMERICAN_EXPRESS ]: 'amexCard',
    [ CREDIT_CARD_TYPE.DISCOVER ]: 'discoverCard',
    [ CREDIT_CARD_TYPE.DEBIT_VISA ]: 'visaCard',
    [ CREDIT_CARD_TYPE.DEBIT_MASTER_CARD ]: 'masterCard'
};

/**
 * @type {object}
 * @desc A React presentation/dumb view component.
 * @example
 * import { paymentCardActivation as PaymentCardActivation } from 'ui/views/widgets/paymentCardActivation';
 *
 * // render
 * <PaymentCardActivation propName='propValue'/>
 */
export const paymentCardActivation = ( props ) => {
    const { card, actions } = props;

    const defaultAction = find( actions, { name: 'setDefault'} );
    const editAction = find( actions, { name: 'edit' } );

    const activateAction = find( actions, { name: 'activate'} );
    const cardActivationInfo = activateAction.cardActivationInfo;

    // check hsaFsa status of card
    const isHsaFsaEnabled = get( card, 'flags.isHsaFsa', false );

    // hsaFsa action toggle
    const hsaFsaAction = find( actions, { name: 'hsaFsa' } );
    const canToggleHsaFsa = get( card, 'actionsVisibility.hsaFsa', false );

    const currentYear = parseInt( moment().format( 'YY' ) );
    let [ expiryMonth, expiryYear ] = split( card.expDate, '/' );
    expiryMonth = trim( expiryMonth );
    expiryYear = trim( expiryYear );

    return (
        <div className='ui-payment-card-activation'>

            {/* header */}
			<div className='ui-payment-card-activation__header'>

                { /* title for the modal */ }
                <div className='ui-payment-card-activation__header__title'>
                    { getLabel( cardActivationInfo.title ) }
                </div>

                { /* close button */ }
                <div
                    className='ui-payment-card-activation__header__close'
                    onClick={ props.onCancelClick }
                >
                    <Icon name='close' width='14' height='14' fill='#ffffff' />
                </div>
            </div>

            {/* body */}
            <div className='ui-payment-card-activation__body'>

                {/* card */}
                <div className='ui-payment-card-activation__body__card'>

                    {/* header */}
                    <div className='ui-payment-card-activation__body__card__header'>

                        {/* title */}
                        <div className='ui-payment-card-activation__body__card__header__title'>
                            { card.name }
                        </div>

                        {/* default label */}
                        <div className='ui-payment-card-activation__body__card__header__default'>
                            <div className='ui-payment-card-activation__body__card__header__default__title'>
                                { getLabel( defaultAction.title ) }
                            </div>
                        </div>
                    </div>

                    {/* digits */}
                    <div className='ui-payment-card-activation__body__card__digits'>
                        <div className='ui-payment-card-activation__body__card__digits__stars'>**** **** ****</div>
                        <div className='ui-payment-card-activation__body__card__digits__numbers'>{ card.lastDigits }</div>
                    </div>

                    {/* expiry section */}
                    <div className='ui-payment-card-activation__body__card__expiry-section'>

                        {/* label */}
                        <div className='ui-payment-card-activation__body__card__expiry-section__label'>
                            { getLabel( GLOBAL_LABELS.DISPLAY_TEXT_EXP_DATE ) }
                        </div>

                        {/* icon */}
                        <div className='ui-payment-card-activation__body__card__expiry-section__icon'>
                            <Icon id={ CARD_TYPE_ICON[ card.type ] } />
                        </div>

                        {
                            isEmpty( hsaFsaAction ) ? null : (
                                <HsaFsaIndicator
                                    { ...hsaFsaAction }
                                    isHsaFsaEnabled = { isHsaFsaEnabled }
                                    onChange={ () => ! canToggleHsaFsa ? null : props.onToggleHsaFsa( { url: hsaFsaAction.url } ) }
                                />
                            )
                        }
                    </div>

                    {/* edit section */}
                    {
                        props.editMode ? (
                            <div className='ui-payment-card-activation__body__card__edit'>
                                <div className='ui-payment-card-activation__body__card__edit__selection'>
                                    <div className='ui-payment-card-activation__body__card__edit__selection__item'>
                                        <SingleSelect
                                            className='ui-payment-card-activation__body__card__edit__selection__item__dropdown'
                                            options={ map( times( 12 ), index => {
                                                const value = 9 < ( index + 1 ) ? index + 1 : `0${ index + 1 }`;
                                                return { name: toString( value ), value: toString( value ), title: { refId: '', value: toString( value ) } };
                                            } ) }
                                            popupWidth={ 100 }
                                            value={ props.expiryMonth }
                                            onChange={ ( { value } ) => props.onCardMonthSelect( { value } ) }
                                        />
                                    </div>
                                    <div className='ui-payment-card-activation__body__card__edit__selection__item'>
                                        <SingleSelect
                                            className='ui-payment-card-activation__body__card__edit__selection__item__dropdown'
                                            options={ map( times( 10 ), index => ( { name: toString( index + currentYear ), value: toString( index + currentYear ), title: { refId: '', value: toString( index + currentYear ) } } ) ) }
                                            popupWidth={ 100 }
                                            value={ props.expiryYear }
                                            onChange={ ( { value } ) => props.onCardYearSelect( { value } ) }
                                        />
                                    </div>
                                </div>
                                <div className='ui-payment-card-activation__body__card__edit__actions'>
                                    <div className='ui-payment-card-activation__body__card__edit__actions__item' onClick={ props.onSubmitEditedDate }>
                                        <Icon id='checkmark' width='10' height='10' fill='#ffffff'/>
                                    </div>
                                    <div className='ui-payment-card-activation__body__card__edit__actions__item' onClick={ props.onCancelEdit }>
                                        <Icon id='cross' width='10' height='10' fill='#ffffff'/>
                                    </div>
                                </div>
                            </div>
                        ) : null
                    }

                    {/* actions section */}
                    {
                        ! props.editMode ? (
                            <div className='ui-payment-card-activation__body__card__actions'>
                                <div className='ui-payment-card-activation__body__card__actions__expiry-date'>
                                    { card.expDate }
                                </div>
                                <div
                                    className='ui-payment-card-activation__body__card__actions__edit'
                                    onClick={ props.onEditActionClick }
                                >
                                    <div className='ui-payment-card-activation__body__card__actions__edit__icon'>
                                        <Icon id='edit' width='20' height='20' />
                                    </div>
                                    <div className='ui-payment-card-activation__body__card__actions__edit__title'>
                                        { getLabel( editAction.title ) }
                                    </div>
                                </div>
                                <div
                                    className='ui-payment-card-activation__body__card__actions__activate'
                                    onClick={ props.showError ? noop : () => props.onActivateActionClick( { url: activateAction.url, id: card.id, month: expiryMonth, year: expiryYear } ) }
                                >
                                    <div className='ui-payment-card-activation__body__card__actions__activate__icon'>
                                        <Icon id='activateCheck' width='20' height='20' />
                                    </div>
                                    <div className='ui-payment-card-activation__body__card__actions__activate__title'>
                                        { getLabel( activateAction.title ) }
                                    </div>
                                </div>
                            </div>
                        ) : null
                    }
                </div>

                {/* error */}
                {
                    props.showError ? (
                        <div className='ui-payment-card-activation__body__error'>
                            { getLabel( activateAction.cardActivationInfo.errorMessage ) }
                        </div>
                    ) : null
                }
            </div>
        </div>
    );
};

// set display name
paymentCardActivation.displayName = 'paymentCardActivation';

// set default props
paymentCardActivation.defaultProps = {};

// prop types
paymentCardActivation.propTypes = {};
